/**
 * The role permissions: what a role lets a person do, and on whose entries.
 *
 * A role is a named set of these (web-redesign-02, Decided 1). Every entry
 * permission has a reach, Off · Own · Their team · Everyone (Decided 2); the
 * others are on or off. The list and which role holds each one by default is
 * `PERMS` in `design/explorations/web-app/_build/sections/business-14-roles.mjs`,
 * with the switches "See everyone's entries" and "Change their own / anyone's
 * entries" folded into the reach of See entries and Change entries, and "Set
 * who reports to whom in their team" from `business-16-reporting.mjs`.
 *
 * Phase 1 carried the keys, and the permissions a level stands for, so `/v1/me`
 * could answer them before any role exists. Phase 2 adds each permission's
 * words and what it needs. The database seeds the same Admin, Member and Viewer
 * sets (`apps/server/drizzle/0024_workspace_roles.sql`, pinned by
 * `apps/server/src/workspace-roles.test.ts`). Phase 3 reads a stored role
 * (`permissionsFrom`), narrows a reach (`narrowerReach`) and derives the old
 * shapes an old phone reads from it (`authorityOfRole`,
 * `tenantCapabilitiesOf`).
 */
import { TENANT_CAPABILITIES, type Authority, type TenantCapability } from './auth';

/** Whose entries an entry permission reaches. `team` is the person and everyone below them in Reports to. */
export const REACHES = ['off', 'own', 'team', 'everyone'] as const;
export type Reach = (typeof REACHES)[number];

export const PERMISSION_GROUPS = ['records', 'insights', 'team', 'workspace', 'os'] as const;
export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

/** One permission: its key, its group, whether it has a reach rather than on and off, and what it needs. */
export type PermissionDef = {
  readonly key: string;
  readonly group: PermissionGroup;
  readonly reach: boolean;
  /**
   * The permissions that must be on (a reach other than Off) before this one
   * can be. The role page draws an unmet one greyed, saying which
   * (`system-12-team.mjs`, "Needs another first").
   */
  readonly needs: readonly string[];
};

/** In the order the role page draws them. */
export const PERMISSIONS = [
  // Records and entries
  { key: 'see_entries', group: 'records', reach: true, needs: [] },
  { key: 'sees_new_records', group: 'records', reach: false, needs: ['see_entries'] },
  { key: 'add_entries', group: 'records', reach: false, needs: ['see_entries'] },
  { key: 'change_entries', group: 'records', reach: true, needs: ['see_entries'] },
  { key: 'delete_entries', group: 'records', reach: true, needs: ['see_entries'] },
  { key: 'download_entries', group: 'records', reach: true, needs: ['see_entries'] },
  { key: 'manage_records', group: 'records', reach: false, needs: ['see_entries'] },
  // Insights
  { key: 'see_insights', group: 'insights', reach: false, needs: [] },
  { key: 'change_insights', group: 'insights', reach: false, needs: ['see_insights'] },
  { key: 'change_dashboard', group: 'insights', reach: false, needs: ['see_insights'] },
  // Money: balances, statements, the Accounts list and the money cards (L27 of docs/plans/ledger.md)
  { key: 'see_money', group: 'insights', reach: false, needs: [] },
  // Links went with the Link engine (step 9.5 of docs/plans/ledger.md): a stored role that still names
  // see_links or manage_links reads as if it did not (`permissionsFrom` reads only the keys here).
  // Team and roles
  { key: 'see_team', group: 'team', reach: false, needs: [] },
  { key: 'set_reports_to', group: 'team', reach: false, needs: ['see_team'] },
  { key: 'invite_people', group: 'team', reach: false, needs: ['see_team'] },
  { key: 'change_access', group: 'team', reach: false, needs: ['see_team'] },
  { key: 'remove_people', group: 'team', reach: false, needs: ['see_team'] },
  { key: 'manage_roles', group: 'team', reach: false, needs: ['see_team'] },
  // Workspace settings
  { key: 'change_workspace', group: 'workspace', reach: false, needs: [] },
  { key: 'download_workspace', group: 'workspace', reach: false, needs: [] },
  // OS
  { key: 'ask_os', group: 'os', reach: false, needs: [] },
  { key: 'os_changes', group: 'os', reach: false, needs: ['ask_os'] },
  { key: 'talk_live', group: 'os', reach: false, needs: ['ask_os'] },
  { key: 'keep_facts', group: 'os', reach: false, needs: ['ask_os'] },
] as const satisfies readonly PermissionDef[];

export type PermissionKey = (typeof PERMISSIONS)[number]['key'];
type ReachKey = Extract<(typeof PERMISSIONS)[number], { reach: true }>['key'];
type SwitchKey = Exclude<PermissionKey, ReachKey>;

/** Every permission's value: a reach for an entry permission, on or off for the rest. */
export type RolePermissions = { [K in ReachKey]: Reach } & { [K in SwitchKey]: boolean };

export const PERMISSION_KEYS: readonly PermissionKey[] = PERMISSIONS.map((one) => one.key);

export const hasReach = (key: PermissionKey): boolean =>
  PERMISSIONS.some((one) => one.key === key && one.reach);

/** The words key of a permission's name, as the role page draws it. */
export const permissionWordsKey = <K extends PermissionKey>(key: K): `role.permission.${K}` => `role.permission.${key}`;

/** The words key of a group's title, and of the line under it. */
export const permissionGroupWordsKey = <G extends PermissionGroup>(group: G): `role.group.${G}` => `role.group.${group}`;
export const permissionGroupLineKey = <G extends PermissionGroup>(group: G): `role.group.${G}.line` =>
  `role.group.${group}.line`;

/** The words key of a reach: Off · Own · Their team · Everyone. */
export const reachWordsKey = <R extends Reach>(reach: R): `role.reach.${R}` => `role.reach.${reach}`;

/** Whether a permission is on: any reach but Off, or a switch that is on. */
export const isOn = (permissions: RolePermissions, key: PermissionKey): boolean => {
  const value = permissions[key];
  return value !== false && value !== 'off';
};

/** What a permission needs, as keys. */
export const needsOf = (key: PermissionKey): readonly PermissionKey[] =>
  (PERMISSIONS.find((one) => one.key === key)?.needs ?? []) as readonly PermissionKey[];

/**
 * The permissions that are on while something they need is off. Empty for a
 * role that holds together; the role page and the routes (phase 4) refuse any
 * other.
 */
export const unmetNeeds = (permissions: RolePermissions): { key: PermissionKey; needs: PermissionKey[] }[] =>
  PERMISSIONS.flatMap((one) => {
    if (!isOn(permissions, one.key)) return [];
    const missing = needsOf(one.key).filter((need) => !isOn(permissions, need));
    return missing.length === 0 ? [] : [{ key: one.key, needs: [...missing] }];
  });

/** The two roles every workspace has, seeded by the database and never removed (Decided 3). */
export const SYSTEM_ROLE_KEYS = ['admin', 'member'] as const;
export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number];

/** The one permission Admin always keeps, so someone can always fix the rest (open question 6). */
export const ADMIN_KEEPS: PermissionKey = 'change_access';

/** Per-field and per-list access on a record (Decided 7): one value each. */
export const ACCESS_LEVELS = ['see', 'change', 'none'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

/** How an invite reaches the person (Decided 10). There is no code-only invite. */
export const INVITE_DELIVERIES = ['link', 'link_code'] as const;
export type InviteDelivery = (typeof INVITE_DELIVERIES)[number];

/**
 * What an ask for a right carries (Decided 11): `data.task` of kind request,
 * whose body names the asker and the right. The database checks the shape
 * (`task_right_ask_shape`); the routes of phase 4 write and answer it, and it
 * reaches everyone whose role holds change_access.
 */
export type RightAsk = {
  readonly ask: 'right';
  /** The user id of the person asking. */
  readonly from: string;
  /** A permission their role lacks, or access to one record. */
  readonly right:
    | { readonly permission: PermissionKey }
    | { readonly recordId: string; readonly access: AccessLevel };
  /** Their own words, when they gave any. */
  readonly note?: string;
};

/** Every permission off: the floor a role is built up from. */
export const noPermissions = (): RolePermissions =>
  Object.fromEntries(PERMISSIONS.map((one) => [one.key, one.reach ? 'off' : false])) as RolePermissions;

/** Every permission on, at Everyone: Admin, and the primary admin. */
export const allPermissions = (): RolePermissions =>
  Object.fromEntries(PERMISSIONS.map((one) => [one.key, one.reach ? 'everyone' : true])) as RolePermissions;

/** The seeded Member role (phase 2.2): what a member may do today, plus See insights. */
export const memberPermissions = (): RolePermissions => ({
  ...noPermissions(),
  see_entries: 'everyone',
  sees_new_records: true,
  add_entries: true,
  change_entries: 'own',
  download_entries: 'everyone',
  see_insights: true,
  see_team: true,
  ask_os: true,
  os_changes: true,
  talk_live: true,
});

/** A look-only role, for today's viewers (phase 2.6): see, never change. */
export const viewerPermissions = (): RolePermissions => ({
  ...noPermissions(),
  see_entries: 'everyone',
  sees_new_records: true,
  see_insights: true,
  ask_os: true,
});

/** The role a level becomes. Owner, partner and admin are Admin; the owner is also the primary admin. */
export const ROLE_OF_AUTHORITY = {
  owner: 'Admin',
  partner: 'Admin',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
} as const satisfies Record<Authority, string>;

/**
 * The permissions a level stands for, until a membership carries a role
 * (phases 2 and 3). The same mapping the backfill uses.
 */
export const permissionsOfAuthority = (authority: Authority): RolePermissions => {
  switch (ROLE_OF_AUTHORITY[authority]) {
    case 'Admin':
      return allPermissions();
    case 'Member':
      return memberPermissions();
    case 'Viewer':
      return viewerPermissions();
  }
};

/** The role fields of a `/v1/me` membership. */
export type MembershipRoleFields = {
  /** The role's id. Null until the membership carries a role. */
  roleId: string | null;
  /** The role's name as the workspace spells it. A name, not a key: roles are the workspace's own. */
  roleName: string;
  /** Whether this person is the primary admin: every permission, and the steps kept for the owner alone. */
  isPrimary: boolean;
  /** What the role lets them do: a reach for an entry permission, on or off for the rest. */
  permissions: RolePermissions;
  /** The user id of the person they report to, or null when nobody. */
  reportsTo: string | null;
};

/**
 * The role fields a membership answers while it carries only a level: no role
 * id yet, the role the level becomes, primary for an owner, nobody above.
 */
export const roleFieldsOfAuthority = (authority: Authority): MembershipRoleFields => ({
  roleId: null,
  roleName: ROLE_OF_AUTHORITY[authority],
  isPrimary: authority === 'owner',
  permissions: permissionsOfAuthority(authority),
  reportsTo: null,
});

/** As much of a person's standing in a workspace as `makesRecords` reads. */
export type RecordMakerStanding = {
  /** Whether their role decides what they may do yet (`app.membership.role_live`). */
  readonly live: boolean;
  readonly isPrimary: boolean;
  readonly permissions: RolePermissions;
  /** The level (`app.membership.authority`), which decides while the role is not live. */
  readonly authority: string;
};

/**
 * Whether a person makes records and changes their fields, and so may copy or
 * delete one: the role's "Make records and change their fields"
 * (`manage_records`) where the role is live, the primary admin holding every
 * permission; the owner alone, as before roles, where it is not yet. No
 * membership makes nothing. The server's `mayMakeRecords` refuses on this,
 * and the web draws Duplicate, Delete and New list on it.
 */
export const makesRecords = (standing: RecordMakerStanding | null): boolean => {
  if (standing === null) return false;
  return standing.live ? standing.isPrimary || isOn(standing.permissions, 'manage_records') : standing.authority === 'owner';
};

/* ------------------------------------------------------------------ phase 3 */

/** Off, then Own, then Their team, then Everyone: each reaches everything the one before it does. */
const REACH_RANK: Readonly<Record<Reach, number>> = { off: 0, own: 1, team: 2, everyone: 3 };

export const isReach = (value: unknown): value is Reach =>
  typeof value === 'string' && (REACHES as readonly string[]).includes(value);

/** The narrower of two reaches: what is left when one limit meets another. */
export const narrowerReach = (a: Reach, b: Reach): Reach => (REACH_RANK[a] <= REACH_RANK[b] ? a : b);

/** Whether reach `a` covers at least what reach `b` does. */
export const reachCovers = (a: Reach, b: Reach): boolean => REACH_RANK[a] >= REACH_RANK[b];

/**
 * A role's permissions as `app.workspace_role.permissions` stores them, read
 * without trusting the document: a key it lacks, or a value of the wrong kind,
 * reads as off (the column note says so). A key this build does not know is
 * dropped.
 */
export const permissionsFrom = (raw: unknown): RolePermissions => {
  const stored = raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return Object.fromEntries(
    PERMISSIONS.map((one) => {
      const value = stored[one.key];
      return [one.key, one.reach ? (isReach(value) ? value : 'off') : value === true];
    }),
  ) as RolePermissions;
};

/** The reach of each entry permission, as the access checks and a newer client's grant summary carry it. */
export type EntryReaches = {
  readonly see: Reach;
  readonly change: Reach;
  readonly delete: Reach;
  readonly download: Reach;
};

export const entryReachesOf = (permissions: RolePermissions): EntryReaches => ({
  see: permissions.see_entries,
  change: permissions.change_entries,
  delete: permissions.delete_entries,
  download: permissions.download_entries,
});

/**
 * Which permission stands for each old tenant capability, so `/v1/me`'s
 * `capabilities` and every route that still asks `holdsTenantCapability` read
 * the role and not the rank (phase 3.1, 3.4). An admin held all seven before (eight until
 * manage_links went in step 9.5 of docs/plans/ledger.md); Admin holds all seven
 * here, so nothing moves for them.
 */
export const TENANT_CAPABILITY_PERMISSION = {
  manage_members: 'change_access',
  define_roles: 'manage_roles',
  manage_units: 'change_workspace',
  manage_policies: 'change_workspace',
  close_period: 'change_workspace',
  manage_billing: 'change_workspace',
  view_business_insights: 'see_insights',
} as const satisfies Record<TenantCapability, PermissionKey>;

/** The old tenant capabilities a set of permissions stands for, in the old list's order. */
export const tenantCapabilitiesOf = (permissions: RolePermissions): TenantCapability[] =>
  TENANT_CAPABILITIES.filter((capability) => isOn(permissions, TENANT_CAPABILITY_PERMISSION[capability]));

/**
 * The permissions that only look. A role holding nothing else changes nothing,
 * and an old phone is told it is a viewer.
 */
const LOOKING: ReadonlySet<PermissionKey> = new Set<PermissionKey>([
  'see_entries',
  'sees_new_records',
  'download_entries',
  'see_insights',
  // Seeing the money (balances, statements) is looking: an accountant who sees it and changes nothing only looks.
  'see_money',
  'see_team',
  'download_workspace',
  'ask_os',
  'talk_live',
]);

export const isLookOnly = (permissions: RolePermissions): boolean =>
  PERMISSION_KEYS.every((key) => LOOKING.has(key) || !isOn(permissions, key));

/**
 * The level an old phone is told (phase 3.4): the primary admin is `owner`,
 * Admin is `admin`, a role that only looks is `viewer`, and every other role
 * is `member`.
 */
export const authorityOfRole = (role: {
  readonly isPrimary: boolean;
  readonly systemKey: SystemRoleKey | null;
  readonly permissions: RolePermissions;
}): Authority => {
  if (role.isPrimary) return 'owner';
  if (role.systemKey === 'admin') return 'admin';
  return isLookOnly(role.permissions) ? 'viewer' : 'member';
};
